import * as React from "react"
import { csrfToken } from "@/lib/csrf"
import { parseMessageTemplate, variablesUsed } from "@/lib/message-template"
import type { SettingPreviewResult } from "@/types/automation"

/**
 * Asks the roster how many members the current target selection would reach,
 * for one real member's details to word the message against, and — when the
 * message has conditions — for the audience grouped by which version each
 * member would get, with a real member from each group.
 *
 * Raw fetch(), not Inertia's router: previewing must not navigate away from a
 * form nobody has saved yet. AGENTS.md permits `render json:` for exactly this.
 *
 * The message is sent, but the request is only made again when the targets
 * change, a condition's field, comparison or value does, or the message starts
 * or stops using a variable. Typing the wording never touches SQL Server — the
 * browser renders it against the members it already has, and measures every
 * member's text from the lengths of their details (milestone 32).
 */
export type PreviewState = {
  loading: boolean
  result: SettingPreviewResult | null
  error: string | null
}

export function useAudiencePreview({
  automationId,
  coversAllLocals,
  selectedTargetIds,
  message,
  language,
}: {
  automationId: number
  coversAllLocals: boolean
  selectedTargetIds: number[]
  message: string
  /** Preview one language's version: every sample prefers this language. */
  language?: string
}): PreviewState {
  const [state, setState] = React.useState<PreviewState>({
    loading: true,
    result: null,
    error: null,
  })

  // A stable key, so re-ordering the same picks doesn't refetch.
  const targetKey = React.useMemo(
    () => `${coversAllLocals}:${[...selectedTargetIds].sort((a, b) => a - b).join(",")}`,
    [coversAllLocals, selectedTargetIds],
  )

  // What the server looks members up and measures them by: the conditions,
  // and which variables the text is filled in from. Wording is left out on
  // purpose.
  const conditionsKey = React.useMemo(() => {
    const template = parseMessageTemplate(message)
    if (!template.valid) return ""
    return [...variablesUsed(template)].sort().join(",") + "|" + template.conditions
      .map((condition) => `${condition.field} ${condition.operator} ${condition.value}`)
      .join("")
  }, [message])

  // Read when the request fires, so a refetch carries the latest wording
  // without the wording itself being a reason to refetch.
  const latestMessage = React.useRef(message)
  latestMessage.current = message

  React.useEffect(() => {
    const controller = new AbortController()
    // Typing in the Locals list changes the selection a checkbox at a time;
    // without this every tick would be its own roster round trip.
    const timer = window.setTimeout(() => {
      setState((current) => ({ ...current, loading: true, error: null }))

      fetch(`/automations/${automationId}/preview`, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          covers_all_locals: coversAllLocals,
          selected_target_ids: selectedTargetIds,
          message: latestMessage.current,
          language: language ?? null,
        }),
      })
        // A refusal the server has words for — a rate limit, a missing roster
        // connection — comes back as a real result carrying `ok: false` and an
        // explanation, whatever the HTTP status. Reading the body first means
        // the person is told why rather than being shown a bare status code.
        .then(async (response) => {
          const body = (await response.json().catch(() => null)) as
            | SettingPreviewResult
            | null
          if (body && typeof body.ok === "boolean") return body
          throw new Error(`Preview failed (${response.status})`)
        })
        .then((result) => setState({ loading: false, result, error: null }))
        .catch((error: Error) => {
          if (error.name === "AbortError") return
          setState({ loading: false, result: null, error: error.message })
        })
    }, 350)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
    // targetKey and conditionsKey stand in for the values they are built from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationId, targetKey, conditionsKey, language])

  return state
}
